import React, {
  useMemo,
  useState,
} from "react";
import {
  api,
  ApiClientError,
} from "../../api";
import { useToast } from "../../context/ToastContext";
import type {
  ClinicalAnatomicalMapPurpose,
  ClinicalAnatomicalMarkerType,
  ClinicalCareProgramDetailResponse,
  ClinicalGeometryType,
} from "../../types";
import {
  ANATOMICAL_TEMPLATES,
  getAnatomicalTemplate,
  type AnatomicalTemplateRegion,
  type AnatomicalTemplateSex,
} from "./anatomicalTemplates";
import {
  REGION_LABELS,
  SEX_LABELS,
  VIEW_LABELS,
  markerFormFromDraft,
  markerPoints,
  sexesForRegion,
  templatesForFilters,
  uniqueRegions,
} from "./anatomicalUsability";
import {
  PremiumAnatomicalCanvas,
  PremiumTreatmentSummary,
} from "./PremiumAnatomicalCanvas";
import {
  SavedAnatomicalMaps,
} from "./SavedAnatomicalMaps";
import {
  administrationAutofill,
  administrationLabel,
  administrationTraceLabel,
  administrationsForSession,
  findAdministration,
} from "./anatomicalTraceability";
import type {
  AnatomicalPoint,
} from "./anatomicalGeometry";
import {
  EMPTY_PREMIUM_MARKER_FORM,
  createPremiumDraftMarker,
  duplicateDraftMarker,
  markerKeyFor,
  type PremiumDraftMarker,
  type PremiumMarkerForm,
} from "./anatomicalDraft";

const PURPOSE_OPTIONS: Array<{
  value: ClinicalAnatomicalMapPurpose;
  label: string;
}> = [
  {
    value: "ASSESSMENT",
    label: "Evaluación",
  },
  {
    value: "DIAGNOSIS",
    label: "Diagnóstico",
  },
  {
    value: "TREATMENT",
    label: "Tratamiento",
  },
  {
    value: "FOLLOW_UP",
    label: "Seguimiento",
  },
];

const MARKER_OPTIONS: Array<{
  value: ClinicalAnatomicalMarkerType;
  label: string;
}> = [
  {
    value: "PROBLEM",
    label: "Problema",
  },
  {
    value: "DIAGNOSIS",
    label: "Diagnóstico",
  },
  {
    value: "TREATMENT",
    label: "Tratamiento",
  },
  {
    value: "OBSERVATION",
    label: "Observación",
  },
];

const GEOMETRY_OPTIONS: Array<{
  value: ClinicalGeometryType;
  label: string;
}> = [
  {
    value: "POINT",
    label: "Punto",
  },
  {
    value: "LINE",
    label: "Línea / vector",
  },
  {
    value: "AREA",
    label: "Área",
  },
];

function geometryInstruction(
  geometryType: ClinicalGeometryType
): string {
  if (geometryType === "POINT") {
    return "Tocá una ubicación.";
  }

  if (geometryType === "LINE") {
    return "Marcá dos o más puntos para formar la línea.";
  }

  return "Marcá tres o más puntos para delimitar el área.";
}

export function PremiumAnatomicalMapWorkspace({
  detail,
  onChanged,
}: {
  detail: ClinicalCareProgramDetailResponse;
  onChanged: () => void | Promise<void>;
}) {
  const { show } = useToast();

  const initialTemplate =
    ANATOMICAL_TEMPLATES[0];

  const [
    templateCode,
    setTemplateCode,
  ] = useState(initialTemplate.code);

  const [templateRegion, setTemplateRegion] =
    useState<AnatomicalTemplateRegion>(initialTemplate.region);
  const [templateSex, setTemplateSex] =
    useState<AnatomicalTemplateSex>(initialTemplate.sex);

  const templateRegions = useMemo(
    () => uniqueRegions(ANATOMICAL_TEMPLATES), []
  );
  const templateSexes = useMemo(
    () => sexesForRegion(ANATOMICAL_TEMPLATES, templateRegion),
    [templateRegion]
  );
  const filteredTemplates = useMemo(
    () => templatesForFilters(ANATOMICAL_TEMPLATES, templateRegion, templateSex),
    [templateRegion, templateSex]
  );

  const template = useMemo(
    () =>
      getAnatomicalTemplate(
        templateCode
      ),
    [templateCode]
  );

  const [purpose, setPurpose] =
    useState<ClinicalAnatomicalMapPurpose>(
      "TREATMENT"
    );

  const [sessionId, setSessionId] =
    useState("");

  const [mapNotes, setMapNotes] =
    useState("");

  const [form, setForm] =
    useState<PremiumMarkerForm>({
      ...EMPTY_PREMIUM_MARKER_FORM,
    });

  const [activePoints, setActivePoints] =
    useState<AnatomicalPoint[]>([]);

  const [markers, setMarkers] =
    useState<PremiumDraftMarker[]>([]);

  const [editingMarkerKey, setEditingMarkerKey] =
    useState<string | null>(null);

  const [submitting, setSubmitting] =
    useState(false);

  const [showAnnotations, setShowAnnotations] =
    useState(true);

  const availableAdministrations = useMemo(
    () => administrationsForSession(detail.administrations, sessionId),
    [detail.administrations, sessionId]
  );

  const selectedAdministration = useMemo(
    () => findAdministration(detail.administrations, form.administrationId),
    [detail.administrations, form.administrationId]
  );

  function updateForm<
    Key extends keyof PremiumMarkerForm
  >(
    key: Key,
    value: PremiumMarkerForm[Key]
  ) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function changeGeometry(
    value: ClinicalGeometryType
  ) {
    updateForm(
      "geometryType",
      value
    );
    setActivePoints([]);
  }

  function addCanvasPoint(
    point: AnatomicalPoint
  ) {
    setActivePoints((current) =>
      form.geometryType === "POINT"
        ? [point]
        : [...current, point]
    );
  }

  function undoPoint() {
    setActivePoints((current) =>
      current.slice(0, -1)
    );
  }

  function addMarker() {
    const result =
      createPremiumDraftMarker({
        template,
        points: activePoints,
        form,
        markerKey: markerKeyFor(
          template,
          markers.length + 1
        ),
      });

    if (!result.valid || !result.marker) {
      show(
        result.message ||
          "No se pudo crear la marca.",
        "error"
      );
      return;
    }

    setMarkers((current) =>
      editingMarkerKey
        ? current.map((marker) =>
            marker.marker_key === editingMarkerKey
              ? { ...(result.marker as PremiumDraftMarker), marker_key: editingMarkerKey }
              : marker
          )
        : [...current, result.marker as PremiumDraftMarker]
    );

    const wasEditing = Boolean(editingMarkerKey);
    setEditingMarkerKey(null);
    setActivePoints([]);

    setForm((current) => ({
      ...EMPTY_PREMIUM_MARKER_FORM,
      markerType:
        current.markerType,
      geometryType:
        current.geometryType,
      zoneCode:
        current.zoneCode,
    }));

    show(
      wasEditing
        ? "Marca actualizada en el borrador."
        : "Marca agregada al borrador.",
      "success"
    );
  }

  function editMarker(marker: PremiumDraftMarker) {
    setEditingMarkerKey(marker.marker_key);
    setForm(markerFormFromDraft(marker));
    setActivePoints(markerPoints(marker));
  }

  function cancelMarkerEdit() {
    setEditingMarkerKey(null);
    setActivePoints([]);
    setForm({ ...EMPTY_PREMIUM_MARKER_FORM });
  }

  function removeMarker(
    markerKey: string
  ) {
    setMarkers((current) =>
      current.filter(
        (marker) => marker.marker_key !== markerKey
      )
    );
    if (editingMarkerKey === markerKey) cancelMarkerEdit();
  }

  function duplicateMarker(
    marker: PremiumDraftMarker
  ) {
    setMarkers((current) => [
      ...current,
      duplicateDraftMarker(
        marker,
        markerKeyFor(
          template,
          current.length + 1
        )
      ),
    ]);
  }

  function resetDraft() {
    setMarkers([]);
    setEditingMarkerKey(null);
    setActivePoints([]);
    setMapNotes("");
    setSessionId("");
    setForm({
      ...EMPTY_PREMIUM_MARKER_FORM,
    });
  }

  async function saveMap() {
    if (markers.length < 1) {
      show(
        "Agregá al menos una marca antes de guardar el mapa.",
        "error"
      );
      return;
    }

    setSubmitting(true);

    try {
      await api.createClinicalAnatomicalMap(
        detail.program.id,
        {
          session_id:
            sessionId || undefined,
          map_type:
            template.mapType,
          view_code:
            template.view,
          purpose,
          template_code:
            template.code,
          template_version:
            template.version,
          notes:
            mapNotes.trim() ||
            undefined,
          patient_visibility:
            "INTERNAL",
          markers,
        }
      );

      show(
        "Mapa anatómico premium creado en borrador.",
        "success"
      );

      resetDraft();
      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo guardar el mapa anatómico.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="premium-map-workspace">
      <div className="chart-list-actions">
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Mapa anatómico premium
          </h3>
          <div className="meta">
            Diagnóstico y planificación
            sobre anatomía normalizada,
            con múltiples marcas.
          </div>
        </div>

        <div className="premium-map-counter">
          {markers.length}{" "}
          {markers.length === 1
            ? "marca"
            : "marcas"}
        </div>
      </div>

      <div className="premium-map-toolbar">
        <label>
          <span>Región anatómica</span>
          <select value={templateRegion} disabled={markers.length > 0}
            onChange={(event: any) => {
              const region = event.target.value as AnatomicalTemplateRegion;
              const sexes = sexesForRegion(ANATOMICAL_TEMPLATES, region);
              const sex = sexes.includes(templateSex) ? templateSex : sexes[0];
              const next = templatesForFilters(ANATOMICAL_TEMPLATES, region, sex)[0];
              setTemplateRegion(region); setTemplateSex(sex);
              if (next) setTemplateCode(next.code);
              setActivePoints([]); updateForm("zoneCode", "");
            }}>
            {templateRegions.map((region) => (
              <option key={region} value={region}>{REGION_LABELS[region]}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Sexo anatómico</span>
          <select value={templateSex} disabled={markers.length > 0}
            onChange={(event: any) => {
              const sex = event.target.value as AnatomicalTemplateSex;
              const next = templatesForFilters(ANATOMICAL_TEMPLATES, templateRegion, sex)[0];
              setTemplateSex(sex); if (next) setTemplateCode(next.code);
              setActivePoints([]); updateForm("zoneCode", "");
            }}>
            {templateSexes.map((sex) => (
              <option key={sex} value={sex}>{SEX_LABELS[sex]}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Vista anatómica</span>
          <select value={templateCode} disabled={markers.length > 0}
            onChange={(event: any) => {
              setTemplateCode(event.target.value); setActivePoints([]);
              updateForm("zoneCode", "");
            }}>
            {filteredTemplates.map((item) => (
              <option key={item.code} value={item.code}>
                {VIEW_LABELS[item.view]} · {item.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Finalidad</span>
          <select
            value={purpose}
            onChange={(event: any) =>
              setPurpose(
                event.target.value
              )
            }
          >
            {PURPOSE_OPTIONS.map(
              (option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              )
            )}
          </select>
        </label>

        <label>
          <span>Sesión vinculada</span>
          <select
            value={sessionId}
            onChange={(event: any) => {
              setSessionId(event.target.value);
              updateForm("administrationId", "");
            }}
          >
            <option value="">
              Sin sesión específica
            </option>
            {detail.sessions.map(
              (session) => (
                <option
                  key={session.id}
                  value={String(
                    session.id
                  )}
                >
                  Sesión{" "}
                  {
                    session.session_number
                  }{" "}
                  ·{" "}
                  {
                    session.session_type
                  }
                </option>
              )
            )}
          </select>
        </label>
      </div>

      <div className="premium-map-layout">
        <div className="premium-map-canvas-panel">
          <div className="premium-map-template-heading">
            <strong>
              {template.label}
            </strong>
            <span className="meta">
              {template.description}
            </span>
          </div>

          <PremiumAnatomicalCanvas
            template={template}
            markers={markers}
            activeType={
              form.geometryType
            }
            activePoints={
              activePoints
            }
            onPoint={
              addCanvasPoint
            }
            showAnnotations={
              showAnnotations
            }
          />

          <div className="premium-map-annotation-controls">
            <label>
              <input
                type="checkbox"
                checked={showAnnotations}
                onChange={(event: any) =>
                  setShowAnnotations(
                    event.target.checked
                  )
                }
              />
              Mostrar dosis en el mapa
            </label>
          </div>

          <PremiumTreatmentSummary
            markers={markers}
          />

          <div className="premium-map-drawing-help">
            <span>
              {geometryInstruction(
                form.geometryType
              )}
            </span>
            <strong>
              {activePoints.length}{" "}
              puntos
            </strong>
          </div>

          <div className="row">
            <button
              className="btn-ghost"
              disabled={
                activePoints.length === 0
              }
              onClick={undoPoint}
            >
              Deshacer punto
            </button>
            <button
              className="btn-ghost"
              disabled={
                activePoints.length === 0
              }
              onClick={() =>
                setActivePoints([])
              }
            >
              Limpiar trazado
            </button>
          </div>
        </div>

        <div className="premium-map-form-panel">
          <div className="chart-form-row">
            <label>
              <span>Tipo clínico</span>
              <select
                value={form.markerType}
                onChange={(event: any) =>
                  updateForm(
                    "markerType",
                    event.target.value
                  )
                }
              >
                {MARKER_OPTIONS.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </label>

            <label>
              <span>Herramienta</span>
              <select
                value={
                  form.geometryType
                }
                onChange={(event: any) =>
                  changeGeometry(
                    event.target.value
                  )
                }
              >
                {GEOMETRY_OPTIONS.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </label>
          </div>

          <label>
            <span>Zona anatómica</span>
            <select
              value={form.zoneCode}
              onChange={(event: any) =>
                updateForm(
                  "zoneCode",
                  event.target.value
                )
              }
            >
              <option value="">
                Seleccionar zona…
              </option>
              {template.zones.map(
                (zone) => (
                  <option
                    key={zone.code}
                    value={zone.code}
                  >
                    {zone.label}
                  </option>
                )
              )}
            </select>
          </label>

          <label>
            <span>
              Descripción de la marca
            </span>
            <input
              placeholder="Ej. adiposidad localizada"
              value={form.label}
              onChange={(event: any) =>
                updateForm(
                  "label",
                  event.target.value
                )
              }
            />
          </label>

          <label>
            <span>
              Diagnóstico o hallazgo
            </span>
            <textarea
              rows={2}
              value={
                form.diagnosisText
              }
              onChange={(event: any) =>
                updateForm(
                  "diagnosisText",
                  event.target.value
                )
              }
            />
          </label>

          <label>
            <span>Administración real vinculada</span>
            <select
              value={form.administrationId}
              disabled={!sessionId}
              onChange={(event: any) => {
                const administration = findAdministration(
                  detail.administrations, event.target.value
                );
                if (!administration) {
                  updateForm("administrationId", "");
                  return;
                }
                setForm((current) => ({
                  ...current,
                  ...administrationAutofill(administration),
                }));
              }}
            >
              <option value="">
                {sessionId ? "Planificación manual / sin administración" : "Seleccioná primero una sesión"}
              </option>
              {availableAdministrations.map((item) => (
                <option key={String(item.id)} value={String(item.id)}>
                  {administrationLabel(item)}
                </option>
              ))}
            </select>
          </label>

          {selectedAdministration && (
            <div className="premium-administration-trace">
              <strong>Trazabilidad confirmada</strong>
              <span>{administrationTraceLabel(selectedAdministration)}</span>
              <span>Registrada por usuario #{String(selectedAdministration.recorded_by_user_id)}</span>
            </div>
          )}

          <div className="chart-form-row">
            <label>
              <span>
                Tratamiento planificado
              </span>
              <input
                placeholder="Ej. Endolifting"
                value={form.treatment}
                onChange={(event: any) =>
                  updateForm(
                    "treatment",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>
                Producto
              </span>
              <input
                placeholder="Producto o insumo"
                value={form.product}
                onChange={(event: any) =>
                  updateForm(
                    "product",
                    event.target.value
                  )
                }
              />
            </label>
          </div>

          <div className="chart-form-row">
            <label>
              <span>Equipo</span>
              <input
                placeholder="Ej. Lumiia 980"
                value={form.equipment}
                onChange={(event: any) =>
                  updateForm(
                    "equipment",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>
                Plano o profundidad
              </span>
              <input
                placeholder="Ej. subdérmico"
                value={form.plane}
                onChange={(event: any) =>
                  updateForm(
                    "plane",
                    event.target.value
                  )
                }
              />
            </label>
          </div>

          <div className="chart-form-row">
            <label>
              <span>Dosis</span>
              <input
                type="number"
                step="any"
                value={form.dose}
                onChange={(event: any) =>
                  updateForm(
                    "dose",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>Unidad</span>
              <input
                placeholder="mg, ml, U"
                value={form.unit}
                onChange={(event: any) =>
                  updateForm(
                    "unit",
                    event.target.value
                  )
                }
              />
            </label>
          </div>

          <label>
            <span>
              Parámetros técnicos
            </span>
            <input
              placeholder="Energía, potencia, frecuencia…"
              value={form.parameters}
              onChange={(event: any) =>
                updateForm(
                  "parameters",
                  event.target.value
                )
              }
            />
          </label>

          <label>
            <span>
              Notas de esta marca
            </span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(event: any) =>
                updateForm(
                  "notes",
                  event.target.value
                )
              }
            />
          </label>

          <button className="btn-primary" onClick={addMarker}>
            {editingMarkerKey ? "Guardar cambios de la marca" : "+ Agregar marca al mapa"}
          </button>
          {editingMarkerKey && (
            <button className="btn-ghost" onClick={cancelMarkerEdit}>
              Cancelar edición
            </button>
          )}
        </div>
      </div>

      {markers.length > 0 && (
        <div className="premium-marker-list">
          <h4>
            Marcas del borrador
          </h4>

          {markers.map(
            (marker, index) => (
              <div
                className="premium-marker-card"
                key={marker.marker_key}
              >
                <div>
                  <strong>
                    {index + 1}.{" "}
                    {marker.label}
                  </strong>
                  {marker.administration_id && (
                    <div className="premium-administration-trace-inline">
                      {administrationTraceLabel(
                        findAdministration(detail.administrations, marker.administration_id)
                      )}
                    </div>
                  )}
                  <div className="meta">
                    {
                      marker.zone_code
                    }{" "}
                    ·{" "}
                    {
                      marker.marker_type
                    }{" "}
                    ·{" "}
                    {
                      marker.geometry_type
                    }{" "}
                    ·{" "}
                    {
                      marker.laterality
                    }
                  </div>
                </div>

                <div className="row">
                  <button className="btn-ghost" onClick={() => editMarker(marker)}>
                    Editar
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() =>
                      duplicateMarker(
                        marker
                      )
                    }
                  >
                    Duplicar
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() =>
                      removeMarker(
                        marker.marker_key
                      )
                    }
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}

      <label className="premium-map-notes">
        <span>
          Notas generales del mapa
        </span>
        <textarea
          rows={3}
          value={mapNotes}
          onChange={(event: any) =>
            setMapNotes(
              event.target.value
            )
          }
        />
      </label>

      <div className="premium-map-footer">
        <button
          className="btn-primary"
          disabled={
            submitting ||
            markers.length === 0
          }
          onClick={saveMap}
        >
          {submitting
            ? "Guardando…"
            : "Guardar mapa en borrador"}
        </button>

        <button
          className="btn-ghost"
          disabled={submitting}
          onClick={resetDraft}
        >
          Descartar borrador
        </button>
      </div>

      <SavedAnatomicalMaps
        detail={detail}
        onChanged={onChanged}
      />
    </div>
  );
}
